import * as CL from '@design-system/component-library';
import { dsClass } from '../../common/constants/dsClasses.js';
import {
  companyBillingInformationMsg,
  customFormErrorFailedMsg,
  firstNameMsg,
  lastNameMsg,
  orderMsg,
  t,
} from '../../common/i18n/index.js';
import { CompanySearch, Email, Name, PhoneNumber } from '../../common/react-hook-form/fields/index.js';
import { formatSum } from '../../common/utils/priceUtils.js';
import { formatPrice } from '../../components/ProductDetails/utils/formatters.js';
import type { CommercialProduct, Price } from '../../generated/api/models.js';

export enum CustomFormType {
  DOMAIN = 'domain',
  OPTION = 'option',
  M365 = 'm365',
}

export interface CustomFormField extends Price {
  id?: string;
  name?: string;
  value?: string;
  isDefault?: boolean;
}

export interface PriceMaps {
  periodicPriceMap?: Map<string, number>;
  onetimePriceMap?: Map<string, number>;
}

interface CustomFormThankYouForOrderProps {
  message: string;
}

interface CustomFormErrorAndButtonProps {
  buttonLabel?: string;
  leadFailed?: boolean;
  leadInProgress?: boolean;
}

export const createPrice = (field: CustomFormField) => {
  return field.monthlyRecurringCharge ? formatPrice(field as CommercialProduct) : formatSum(field.oneTimeCharge);
};

export const createPrices = (periodicPriceMap: Map<string, number>, onetimePriceMap: Map<string, number>) => {
  return {
    periodicPriceMap,
    onetimePriceMap,
    periodicPrice: formatPrice({
      payments: 24,
      monthlyRecurringCharge: Array.from(periodicPriceMap.values()).reduce((a, b) => a + b, 0),
    } as CommercialProduct),
    onetimePrice: formatSum(Array.from(onetimePriceMap.values()).reduce((a, b) => a + b, 0)),
  };
};

export const updatePrices = (
  setValue: (key: string, value: Map<string, number> | string | undefined) => void,
  periodicPriceMap: Map<string, number>,
  onetimePriceMap: Map<string, number>
) => {
  const prices = createPrices(periodicPriceMap, onetimePriceMap);
  setValue('periodicPriceMap', periodicPriceMap);
  setValue('onetimePriceMap', onetimePriceMap);
  setValue('periodicPrice', prices.periodicPrice);
  setValue('onetimePrice', prices.onetimePrice);
};

export const CustomFormContactDetails = () => {
  return (
    <>
      <Name name="firstName" label={t.AIK7(firstNameMsg)} placeholder="" />
      <Name name="lastName" label={t.Y41S(lastNameMsg)} placeholder="" />
      <Email placeholder="" />
      <PhoneNumber placeholder="" />
    </>
  );
};

export const CompanyAndContactDetails = ({ enableCompanyFields }: { enableCompanyFields?: boolean }) => (
  <>
    {enableCompanyFields && (
      <>
        <h3 className="ds-h4">{t.IRQS(companyBillingInformationMsg)}</h3>
        <CompanySearch placeholder="" />
      </>
    )}
    <CustomFormContactDetails />
  </>
);

export const CustomFormThankYouForOrder = ({ message }: CustomFormThankYouForOrderProps) => {
  return (
    <div className="of-custom-form">
      <div className={`${dsClass.GRID_ROW} ${dsClass.JUSTIFY_CONTENT_CENTER}`}>
        <CL.Icon color="green-600" icon="check" size="xl" type="filled" />
        <h3 className={`${dsClass.H4} ${dsClass.PADDING_3}`}>{message}</h3>
      </div>
    </div>
  );
};

export const CustomFormErrorAndButton = ({
  buttonLabel,
  leadFailed,
  leadInProgress,
}: CustomFormErrorAndButtonProps) => {
  return (
    <>
      {leadFailed && (
        <CL.Notification hasCloseButton={false} text={t.DLRT(customFormErrorFailedMsg)} type="danger" visible />
      )}
      <CL.Button className={dsClass.MARGIN_TOP_3} loading={leadInProgress} size="l" type="submit">
        {buttonLabel ?? t.AQOL(orderMsg)}
      </CL.Button>
    </>
  );
};
